/**
 * Google Maps API Cache Service
 * 
 * Provides intelligent caching for expensive Google Maps API calls:
 * - Geocoding API (address to lat/lng)
 * - Distance Matrix API (distance/duration between locations)
 * 
 * Features:
 * - localStorage persistence with expiration
 * - Memory cache for session performance
 * - Automatic cleanup of expired entries
 * - Cache statistics and monitoring
 * - Configurable TTL (Time To Live)
 */

import logger from '../utils/logger';

// Cache configuration
const CACHE_CONFIG = {
  // Storage keys
  GEOCODING_KEY: 'gmaps_geocoding_cache',
  DISTANCE_KEY: 'gmaps_distance_cache',
  STATS_KEY: 'gmaps_cache_stats',
  
  // TTL (Time To Live) in milliseconds
  GEOCODING_TTL: 30 * 24 * 60 * 60 * 1000, // 30 days for geocoding
  DISTANCE_TTL: 7 * 24 * 60 * 60 * 1000,    // 7 days for distance/routes
  
  // Cache limits
  MAX_GEOCODING_ENTRIES: 1000,
  MAX_DISTANCE_ENTRIES: 500,
  
  // Cleanup thresholds
  CLEANUP_THRESHOLD: 0.9, // Clean when 90% full
  CLEANUP_PERCENTAGE: 0.3 // Remove 30% of oldest entries
};

// In-memory cache for session performance
const memoryCache = {
  geocoding: new Map(),
  distance: new Map()
};

// Cache statistics
let cacheStats = {
  geocoding: { hits: 0, misses: 0, errors: 0 },
  distance: { hits: 0, misses: 0, errors: 0 },
  lastCleanup: Date.now()
};

/**
 * Normalize address string for consistent cache keys
 * @param {string} address - Raw address string
 * @returns {string} Normalized address
 */
const normalizeAddress = (address) => {
  if (!address || typeof address !== 'string') return '';
  
  return address
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ') // Multiple spaces to single space
    .replace(/[^\w\s,.-]/g, '') // Remove special characters except common ones
    .replace(/\b(street|st|avenue|ave|road|rd|boulevard|blvd|lane|ln|drive|dr|court|ct|place|pl)\b/g, match => {
      // Normalize common street abbreviations
      const abbrevMap = {
        'street': 'st', 'avenue': 'ave', 'road': 'rd', 'boulevard': 'blvd',
        'lane': 'ln', 'drive': 'dr', 'court': 'ct', 'place': 'pl'
      };
      return abbrevMap[match] || match;
    });
};

/**
 * Generate cache key for distance matrix requests
 * @param {Object} origin - Origin location {lat, lng}
 * @param {Array} destinations - Array of destination locations
 * @returns {string} Cache key
 */
const generateDistanceKey = (origin, destinations) => {
  if (!origin || !destinations || !Array.isArray(destinations)) return '';
  
  const originKey = `${origin.lat.toFixed(6)},${origin.lng.toFixed(6)}`;
  const destKeys = destinations
    .map(dest => `${dest.lat.toFixed(6)},${dest.lng.toFixed(6)}`)
    .sort() // Sort to ensure consistent key regardless of order
    .join('|');
  
  return `${originKey}->${destKeys}`;
};

/**
 * Check if localStorage is available and working
 * @returns {boolean} True if localStorage is available
 */
const isLocalStorageAvailable = () => {
  try {
    const test = 'gmaps_cache_test';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    logger.log('localStorage not available:', e);
    return false;
  }
};

/**
 * Get data from localStorage with error handling
 * @param {string} key - Storage key
 * @returns {Object|null} Parsed data or null
 */
const getFromStorage = (key) => {
  if (!isLocalStorageAvailable()) return null;
  
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (e) {
    logger.log(`Error reading from localStorage (${key}):`, e);
    return null;
  }
};

/**
 * Save data to localStorage with error handling
 * @param {string} key - Storage key
 * @param {Object} data - Data to save
 * @returns {boolean} True if successful
 */
const saveToStorage = (key, data) => {
  if (!isLocalStorageAvailable()) return false;
  
  try {
    localStorage.setItem(key, JSON.stringify(data));
    return true;
  } catch (e) {
    logger.log(`Error writing to localStorage (${key}):`, e);
    // If storage is full, try cleanup and retry
    if (e.name === 'QuotaExceededError') {
      cleanupExpiredEntries();
      try {
        localStorage.setItem(key, JSON.stringify(data));
        return true;
      } catch (retryError) {
        logger.log('Failed to save after cleanup:', retryError);
        return false;
      }
    }
    return false;
  }
};

/**
 * Clean up expired cache entries
 */
const cleanupExpiredEntries = () => {
  logger.log('Starting cache cleanup...');
  const now = Date.now();
  
  // Clean geocoding cache
  const geocodingCache = getFromStorage(CACHE_CONFIG.GEOCODING_KEY) || {};
  const geocodingKeys = Object.keys(geocodingCache);
  let geocodingCleaned = 0;
  
  geocodingKeys.forEach(key => {
    const entry = geocodingCache[key];
    if (!entry || !entry.timestamp || (now - entry.timestamp) > CACHE_CONFIG.GEOCODING_TTL) {
      delete geocodingCache[key];
      geocodingCleaned++;
    }
  });
  
  if (geocodingCleaned > 0) {
    saveToStorage(CACHE_CONFIG.GEOCODING_KEY, geocodingCache);
  }
  
  // Clean distance cache
  const distanceCache = getFromStorage(CACHE_CONFIG.DISTANCE_KEY) || {};
  const distanceKeys = Object.keys(distanceCache);
  let distanceCleaned = 0;
  
  distanceKeys.forEach(key => {
    const entry = distanceCache[key];
    if (!entry || !entry.timestamp || (now - entry.timestamp) > CACHE_CONFIG.DISTANCE_TTL) {
      delete distanceCache[key];
      distanceCleaned++;
    }
  });
  
  if (distanceCleaned > 0) {
    saveToStorage(CACHE_CONFIG.DISTANCE_KEY, distanceCache);
  }
  
  // Update cleanup timestamp
  cacheStats.lastCleanup = now;
  saveToStorage(CACHE_CONFIG.STATS_KEY, cacheStats);
  
  logger.log(`Cache cleanup completed: ${geocodingCleaned} geocoding entries, ${distanceCleaned} distance entries removed`);
};

/**
 * Update cache statistics
 * @param {string} type - Cache type ('geocoding' or 'distance')
 * @param {string} result - Result type ('hit', 'miss', or 'error')
 */
const updateStats = (type, result) => {
  if (cacheStats[type] && cacheStats[type][result] !== undefined) {
    cacheStats[type][result]++;
    
    // Periodically save stats to localStorage
    if ((cacheStats[type].hits + cacheStats[type].misses) % 10 === 0) {
      saveToStorage(CACHE_CONFIG.STATS_KEY, cacheStats);
    }
  }
};

/**
 * Geocoding Cache Functions
 */

/**
 * Get cached geocoding result
 * @param {string} address - Address to lookup
 * @returns {Object|null} Cached location or null
 */
const getCachedGeocode = (address) => {
  const normalizedAddress = normalizeAddress(address);
  logger.log(`📍 getCachedGeocode: original="${address}" normalized="${normalizedAddress}"`);
  
  if (!normalizedAddress) {
    logger.log(`❌ getCachedGeocode: Address normalization failed`);
    return null;
  }
  
  // Check memory cache first
  if (memoryCache.geocoding.has(normalizedAddress)) {
    logger.log(`🎯 getCachedGeocode: Memory cache hit for "${normalizedAddress}"`);
    updateStats('geocoding', 'hits');
    return memoryCache.geocoding.get(normalizedAddress);
  }
  
  // Check localStorage cache
  const geocodingCache = getFromStorage(CACHE_CONFIG.GEOCODING_KEY) || {};
  const cacheEntry = geocodingCache[normalizedAddress];
  
  logger.log(`💾 getCachedGeocode: localStorage lookup for "${normalizedAddress}"`, cacheEntry ? 'FOUND' : 'NOT FOUND');
  
  if (cacheEntry && cacheEntry.timestamp) {
    const now = Date.now();
    const age = now - cacheEntry.timestamp;
    const ttl = CACHE_CONFIG.GEOCODING_TTL;
    
    logger.log(`⏰ getCachedGeocode: Cache entry age=${age}ms, TTL=${ttl}ms, expired=${age >= ttl}`);
    
    if (age < ttl) {
      // Cache hit - add to memory cache and return
      memoryCache.geocoding.set(normalizedAddress, cacheEntry.data);
      updateStats('geocoding', 'hits');
      logger.log(`✅ getCachedGeocode: Valid cache entry found`, cacheEntry.data);
      return cacheEntry.data;
    } else {
      // Expired entry - remove it
      logger.log(`🗑️ getCachedGeocode: Removing expired cache entry`);
      delete geocodingCache[normalizedAddress];
      saveToStorage(CACHE_CONFIG.GEOCODING_KEY, geocodingCache);
    }
  }
  
  updateStats('geocoding', 'misses');
  logger.log(`❌ getCachedGeocode: Cache miss for "${normalizedAddress}"`);
  return null;
};

/**
 * Cache geocoding result
 * @param {string} address - Original address
 * @param {Object} location - Geocoded location {lat, lng}
 * @returns {boolean} True if cached successfully
 */
const cacheGeocode = (address, location) => {
  const normalizedAddress = normalizeAddress(address);
  logger.log(`💾 cacheGeocode: original="${address}" normalized="${normalizedAddress}"`, location);
  
  if (!normalizedAddress || !location) {
    logger.log(`❌ cacheGeocode: Invalid input - normalizedAddress="${normalizedAddress}" location=${!!location}`);
    return false;
  }
  
  const cacheEntry = {
    data: location,
    timestamp: Date.now(),
    originalAddress: address
  };
  
  logger.log(`📝 cacheGeocode: Creating cache entry`, cacheEntry);
  
  // Add to memory cache
  memoryCache.geocoding.set(normalizedAddress, location);
  logger.log(`🧠 cacheGeocode: Added to memory cache, total entries: ${memoryCache.geocoding.size}`);
  
  // Add to localStorage cache
  const geocodingCache = getFromStorage(CACHE_CONFIG.GEOCODING_KEY) || {};
  geocodingCache[normalizedAddress] = cacheEntry;
  
  // Check cache size and cleanup if needed
  const cacheSize = Object.keys(geocodingCache).length;
  if (cacheSize > CACHE_CONFIG.MAX_GEOCODING_ENTRIES * CACHE_CONFIG.CLEANUP_THRESHOLD) {
    logger.log('Geocoding cache size limit reached, triggering cleanup');
    cleanupExpiredEntries();
  }
  
  return saveToStorage(CACHE_CONFIG.GEOCODING_KEY, geocodingCache);
};

/**
 * Distance Matrix Cache Functions
 */

/**
 * Get cached distance matrix result
 * @param {Object} origin - Origin location {lat, lng}
 * @param {Array} destinations - Array of destination locations
 * @returns {Object|null} Cached distance data or null
 */
const getCachedDistance = (origin, destinations) => {
  const cacheKey = generateDistanceKey(origin, destinations);
  if (!cacheKey) return null;
  
  // Check memory cache first
  if (memoryCache.distance.has(cacheKey)) {
    updateStats('distance', 'hits');
    return memoryCache.distance.get(cacheKey);
  }
  
  // Check localStorage cache
  const distanceCache = getFromStorage(CACHE_CONFIG.DISTANCE_KEY) || {};
  const cacheEntry = distanceCache[cacheKey];
  
  if (cacheEntry && cacheEntry.timestamp) {
    const now = Date.now();
    const age = now - cacheEntry.timestamp;
    
    if (age < CACHE_CONFIG.DISTANCE_TTL) {
      // Cache hit - add to memory cache and return
      memoryCache.distance.set(cacheKey, cacheEntry.data);
      updateStats('distance', 'hits');
      return cacheEntry.data;
    } else {
      // Expired entry - remove it
      delete distanceCache[cacheKey];
      saveToStorage(CACHE_CONFIG.DISTANCE_KEY, distanceCache);
    }
  }
  
  updateStats('distance', 'misses');
  return null;
};

/**
 * Cache distance matrix result
 * @param {Object} origin - Origin location {lat, lng}
 * @param {Array} destinations - Array of destination locations
 * @param {Object} distanceData - Distance matrix API response
 * @returns {boolean} True if cached successfully
 */
const cacheDistance = (origin, destinations, distanceData) => {
  const cacheKey = generateDistanceKey(origin, destinations);
  if (!cacheKey || !distanceData) return false;
  
  const cacheEntry = {
    data: distanceData,
    timestamp: Date.now(),
    origin,
    destinationCount: destinations.length
  };
  
  // Add to memory cache
  memoryCache.distance.set(cacheKey, distanceData);
  
  // Add to localStorage cache
  const distanceCache = getFromStorage(CACHE_CONFIG.DISTANCE_KEY) || {};
  distanceCache[cacheKey] = cacheEntry;
  
  // Check cache size and cleanup if needed
  const cacheSize = Object.keys(distanceCache).length;
  if (cacheSize > CACHE_CONFIG.MAX_DISTANCE_ENTRIES * CACHE_CONFIG.CLEANUP_THRESHOLD) {
    logger.log('Distance cache size limit reached, triggering cleanup');
    cleanupExpiredEntries();
  }
  
  return saveToStorage(CACHE_CONFIG.DISTANCE_KEY, distanceCache);
};

/**
 * Cache Management Functions
 */

/**
 * Get cache statistics
 * @returns {Object} Cache statistics and info
 */
const getCacheStats = () => {
  const geocodingCache = getFromStorage(CACHE_CONFIG.GEOCODING_KEY) || {};
  const distanceCache = getFromStorage(CACHE_CONFIG.DISTANCE_KEY) || {};
  
  return {
    ...cacheStats,
    storage: {
      geocoding: {
        entries: Object.keys(geocodingCache).length,
        maxEntries: CACHE_CONFIG.MAX_GEOCODING_ENTRIES,
        memoryEntries: memoryCache.geocoding.size
      },
      distance: {
        entries: Object.keys(distanceCache).length,
        maxEntries: CACHE_CONFIG.MAX_DISTANCE_ENTRIES,
        memoryEntries: memoryCache.distance.size
      }
    },
    config: {
      geocodingTTL: CACHE_CONFIG.GEOCODING_TTL,
      distanceTTL: CACHE_CONFIG.DISTANCE_TTL
    }
  };
};

/**
 * Clear all caches
 * @param {string} type - Cache type to clear ('all', 'geocoding', 'distance')
 */
const clearCache = (type = 'all') => {
  try {
    if (type === 'all' || type === 'geocoding') {
      localStorage.removeItem(CACHE_CONFIG.GEOCODING_KEY);
      memoryCache.geocoding.clear();
      cacheStats.geocoding = { hits: 0, misses: 0, errors: 0 };
      logger.log('Geocoding cache cleared');
    }
    
    if (type === 'all' || type === 'distance') {
      localStorage.removeItem(CACHE_CONFIG.DISTANCE_KEY);
      memoryCache.distance.clear();
      cacheStats.distance = { hits: 0, misses: 0, errors: 0 };
      logger.log('Distance cache cleared');
    }
    
    if (type === 'all') {
      localStorage.removeItem(CACHE_CONFIG.STATS_KEY);
      cacheStats.lastCleanup = Date.now();
      logger.log('All caches cleared');
    }
    
    return true;
  } catch (error) {
    logger.log('Error clearing cache:', error);
    return false;
  }
};

/**
 * Initialize cache service
 */
const initializeCache = () => {
  // Load existing stats
  const savedStats = getFromStorage(CACHE_CONFIG.STATS_KEY);
  if (savedStats) {
    cacheStats = { ...cacheStats, ...savedStats };
  }
  
  // Run cleanup if it's been more than 24 hours
  const now = Date.now();
  const timeSinceCleanup = now - (cacheStats.lastCleanup || 0);
  const twentyFourHours = 24 * 60 * 60 * 1000;
  
  if (timeSinceCleanup > twentyFourHours) {
    logger.log('Running scheduled cache cleanup');
    cleanupExpiredEntries();
  }
  
  logger.log('Google Maps cache service initialized');
};

// Initialize on import
initializeCache();

// Export cache service
export default {
  // Geocoding functions
  getCachedGeocode,
  cacheGeocode,
  
  // Distance functions
  getCachedDistance,
  cacheDistance,
  
  // Management functions
  getCacheStats,
  clearCache,
  cleanupExpiredEntries,
  
  // Utility functions
  normalizeAddress,
  generateDistanceKey,
  
  // Config
  config: CACHE_CONFIG
};